import type {NodeProvidedProps} from '@visx/network/lib/types';
import type {FC} from 'react';
import React from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../../../components/common/boxedIcon';
import type {InboxGraphAnyInboxNode, InboxGraphInboxNode, InboxGraphMainInboxNode} from '../inboxGraphTypes';
import {getNodeHeight} from '../inboxGraphUtils';
import type {RectangleNodeProps} from './rectangleNodeComponent';
import {RectangleNodeComponent} from './rectangleNodeComponent';

/*
 * Types.
 */

interface InboxIconProps {
  size: VisualSizesEnum;
  color: string;
  backgroundColor: string;
  borderColor?: string;
  borderStyle?: string;
}

interface InboxLabelProps {
  label: string;
  size: string;
  weight?: number;
}

/*
 * Style.
 */

const StyledLabel = styled.div<{$fontSize: string; $fontWeight: number}>`
  padding: 0 12px;
  width: 100%;
  margin-top: 8px;
  text-align: center;
  font-size: ${(p) => p.$fontSize};
  line-height: 18px;
  font-weight: ${(p) => p.$fontWeight};
  ${ellipsis()};
`;

/*
 * Helpers.
 */

function getInboxBackgroundColor(color: string | undefined, theme: UserTheme) {
  return (color !== '#FFFFFF' && color) || theme.greys.shade50;
}

const renderInboxNode = (
  rectangleProps: RectangleNodeProps,
  labelProps: InboxLabelProps,
  iconProps: InboxIconProps,
) => {
  const {size, color, backgroundColor} = iconProps;
  return (
    <RectangleNodeComponent {...rectangleProps} hasShadow>
      <BoxedIcon
        name="inboxFilled"
        size={size}
        color={color}
        backgroundColor={backgroundColor}
        borderColor={iconProps.borderColor}
        borderStyle={iconProps.borderStyle}
      />
      <StyledLabel $fontSize={labelProps.size} $fontWeight={labelProps.weight || fontWeights.normal}>
        {labelProps.label}
      </StyledLabel>
    </RectangleNodeComponent>
  );
};

/*
 * Component.
 */

/**
 * Returns a node representing an inbox in an inbox graph.
 */
export const InboxNodeComponent: FC<NodeProvidedProps<InboxGraphInboxNode>> = ({node}) => {
  const theme = useUserTheme();
  const nodeHeight = getNodeHeight(node);
  const rectangleProps: RectangleNodeProps = {
    height: nodeHeight,
    defaultFill: theme.greys.white,
    hoveredFill: theme.greys.shade30,
    onClick: node.onClick,
    tooltipContent: node.label,
  };

  const iconProps = {
    size: VisualSizesEnum.SMALL,
    color: theme.greys.white,
    backgroundColor: getInboxBackgroundColor(node.iconColor, theme),
  };

  const labelProps = {
    label: node.label,
    size: fontSizes.small,
    weight: fontWeights.medium,
  };

  return renderInboxNode(rectangleProps, labelProps, iconProps);
};

/**
 * Returns a node representing the main inbox in an inbox graph.
 */
export const MainInboxNodeComponent: FC<NodeProvidedProps<InboxGraphMainInboxNode>> = ({node}) => {
  const theme = useUserTheme();
  const nodeHeight = getNodeHeight(node);
  const rectangleProps: RectangleNodeProps = {
    height: nodeHeight,
    defaultFill: theme.greys.white,
    tooltipContent: node.label,
  };

  const iconProps = {
    size: VisualSizesEnum.LARGE,
    color: theme.greys.white,
    backgroundColor: getInboxBackgroundColor(node.iconColor, theme),
  };

  const labelProps = {
    label: node.label,
    size: fontSizes.medium,
    weight: fontWeights.bold,
  };

  return renderInboxNode(rectangleProps, labelProps, iconProps);
};

/**
 * Returns a node representing any inbox in an inbox graph.
 */
export const AnyInboxNodeComponent: FC<NodeProvidedProps<InboxGraphAnyInboxNode>> = ({node}) => {
  const theme = useUserTheme();
  const nodeHeight = getNodeHeight(node);
  const rectangleProps: RectangleNodeProps = {
    height: nodeHeight,
    defaultFill: theme.greys.white,
    tooltipContent: node.label,
  };

  const iconProps = {
    size: VisualSizesEnum.SMALL,
    color: theme.greys.shade60,
    backgroundColor: theme.greys.white,
    borderColor: theme.greys.shade60,
    borderStyle: 'dashed',
  };

  const labelProps = {
    label: node.label,
    size: fontSizes.small,
    weight: fontWeights.medium,
  };

  return renderInboxNode(rectangleProps, labelProps, iconProps);
};
